// eslint-disable-next-line no-restricted-imports
import { mergeWith } from 'lodash-es';

import type { DeepPartial } from '../types/utility-types';

import type { MergedThemeTypes, TBrand, UnionedThemeTypes } from './basic-types';

const internalCache = {} as Record<TBrand, MergedThemeTypes>;
export const registeredThemes = internalCache as Readonly<typeof internalCache>;

export function registerTheme(theme: UnionedThemeTypes) {
  internalCache[theme.key] = theme as MergedThemeTypes;
}

function isFunction(value: unknown): value is Function {
  return typeof value === 'function';
}

function merge(first: any, second: any) {
  return mergeWith(first, second, (target, source) => {
    if (isFunction(target) && isFunction(source)) {
      return (...args: any[]) => {
        // Call the parent first, allowing the child to override
        const result = target(...args);
        const childResult = source(...args);

        // If either result is an object, merge them, otherwise use the child result
        if (typeof result === 'object' && typeof childResult === 'object') {
          return mergeWith(result, childResult);
        }

        return childResult;
      };
    }
    // Use default lodash behavior for other cases
    return undefined;
  });
}

/**
 * Given a list of themes, return a merged theme.
 */
export function mergeThemes(...themes: UnionedThemeTypes[]): MergedThemeTypes {
  return themes.reduce((acc, theme) => merge(acc, theme));
}

/**
 * Given a partial theme, override an existing theme.
 */
export function overrideTheme(
  theme: MergedThemeTypes,
  overrides: DeepPartial<MergedThemeTypes>
): MergedThemeTypes {
  return merge(theme, overrides);
}
